import type { Page } from '@zoomcatalog/design-templates'
import { ProductCompressionService } from '@/services/product-compression-service'
import { Product, ProductNestedData } from '@/store/interfaces'
import { useCommit } from '@/store/rootStore'

export function isProduct(maybeProduct: unknown): maybeProduct is Product {
	return (
		typeof maybeProduct === 'object' &&
		maybeProduct !== null &&
		'slot' in maybeProduct &&
		'product_id' in maybeProduct &&
		'name' in maybeProduct
	)
}

export function useProductStore() {
	const commit = useCommit()

	function addProduct(product: Product, page: Page) {
		commit({ type: 'page/addProduct', payload: { product, page } })
	}

	function deleteProduct(data: { pageId: string; slotId: number }, page: Page) {
		commit({
			type: 'page/removeProduct',
			payload: { pageId: data.pageId, slotId: data.slotId, page },
		})
	}

	function getProduct(maybeProduct: unknown) {
		return ProductProxy(maybeProduct)
	}

	function getDecompressedProduct(maybeProduct: unknown): Product {
		const proxy = ProductProxy(maybeProduct)

		const variations = proxy.variations
		const assets_for_virtuals = proxy.assets_for_virtuals

		return JSON.parse(
			JSON.stringify({
				...proxy,
				variations,
				assets_for_virtuals,
			})
		) as Product
	}

	return {
		addProduct,
		deleteProduct,
		getProduct,
		getDecompressedProduct,
	}
}

export const ProductProxy = (product: unknown) => {
	if (!product) {
		return null as any
	}

	const { data } = product as ProductNestedData
	const productData = product as Product
	return new Proxy<Product>(data ?? productData, {
		get: (target: any, name: keyof Product) => {
			if (
				[
					'product_id',
					'part_id',
					'image',
					'product_name',
					'name',
					'price',
					'variations',
					'assets_for_virtuals',
				].includes(name)
			) {
				if (name in target) {
					if (name === 'variations' && target[name]) {
						const variations = target[name]
						if (typeof variations === 'string') {
							return ProductCompressionService.decompressVariations(variations)
						}
						return variations
					}

					if (name === 'assets_for_virtuals' && target[name]) {
						const assets = target[name]
						if (typeof assets === 'string') {
							return ProductCompressionService.decompressAssetsForVirtuals(assets)
						}
						return assets
					}

					return target[name]
				}

				switch (name) {
					case 'product_id':
						return target?.id ?? target.product_id ?? ''
					case 'name':
						return target.name ?? target.product_name ?? ''
					case 'product_name':
						return target.product_name ?? target.name ?? ''
					case 'part_id':
						return target.sku
					case 'image':
						return target.image_url
					case 'variations':
						return target.variations ?? []
					case 'assets_for_virtuals':
						return target.assets_for_virtuals ?? []
					default:
						return target[name]
				}
			}

			return target[name]
		},
	})
}
